// El pie de una tabla que pagina en el servidor y acumula páginas (ESTANDAR-TABLAS §5.4).
//
// Es UN solo lugar: el contador del resultado, los controles de página sobre lo ya cargado, el
// «Cargar más» que trae la siguiente y el motivo por el que esa siguiente falló. Entre una pantalla
// y otra lo único que cambia es el sustantivo.
import type { Table } from '@tanstack/react-table';
import { DataTablePagination } from '@/kit';
import { cn } from '@/lib/cn';
import { LoadMoreButton } from './LoadMoreButton';
import {
    footerCountLabel,
    loadedCountTitle,
    nextPageFailureNote,
    type LoadedNoun,
    type LoadedScope,
} from './loadedScope';

export interface LoadedScopeFooterProps<TData> {
    /** El sustantivo de las filas: lo único que distingue un pie de otro. */
    noun: LoadedNoun;
    /** Cuánto se lleva cargado y si quedan páginas. `null` = todavía no hay lista que contar. */
    scope: LoadedScope | null;
    /** Cuántas filas pasan los filtros sobre lo cargado. `null` = todavía no hay lista. */
    shown: number | null;
    /** La lectura falló y no hay nada que contar: el contador dice eso en vez de «Cargando…». */
    readFailed?: boolean;
    /** El motor de la tabla: de él salen los controles de página sobre lo YA cargado. */
    table: Table<TData>;
    /** Quedan páginas en el servidor: se ofrece traerlas. */
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    onLoadMore: () => void;
    /** Por qué falló la ÚLTIMA página pedida. `null` = no falló. */
    nextPageError?: string | null;
    /** Desglose junto al contador, p. ej. «3 modelos · 7 variantes». */
    countDetail?: string | null;
    className?: string;
}

export function LoadedScopeFooter<TData>({
    noun,
    scope,
    shown,
    readFailed = false,
    table,
    hasNextPage,
    isFetchingNextPage,
    onLoadMore,
    nextPageError = null,
    countDetail = null,
    className,
}: LoadedScopeFooterProps<TData>) {
    // El motivo va SOBRE la fila y no al lado: al lado empuja los controles y se corta solo.
    const failureNote = nextPageError === null ? null : nextPageFailureNote(nextPageError, noun);
    // Los controles cortan lo cargado, que mientras falten páginas no es el listado entero.
    const pagesTitle = scope === null ? undefined : loadedCountTitle(undefined, scope, noun);

    return (
        <div className={cn('flex-none border-t border-border', className)}>
            {failureNote === null ? null : (
                <p role="alert" className="px-3 pt-1.5 text-[11px] leading-snug text-danger">
                    {failureNote}
                </p>
            )}
            <div className="flex flex-wrap items-center justify-between gap-x-3 px-3">
                <span
                    aria-live="polite"
                    className="text-[10.5px] font-semibold text-muted-foreground"
                >
                    {footerCountLabel(shown, scope, noun, readFailed)}
                    {countDetail === null ? null : (
                        <span className="font-normal" data-testid="footer-count-detail">
                            {` · ${countDetail}`}
                        </span>
                    )}
                </span>
                <div className="flex items-center gap-2" title={pagesTitle}>
                    <DataTablePagination table={table} />
                    {hasNextPage ? (
                        <LoadMoreButton
                            noun={noun}
                            isLoading={isFetchingNextPage}
                            hasFailed={nextPageError !== null}
                            onLoadMore={onLoadMore}
                        />
                    ) : null}
                </div>
            </div>
        </div>
    );
}
